import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatEuro, formatDateFr } from "@/lib/format";
import { Coins, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ALLOCATION_KEY_LABELS, CHARGE_CATEGORY_LABELS } from "@/constants/patrimoine";

const EMPTY = {
  label: "", amount: "", category: "taxe_fonciere",
  allocation_key: "surface", period_start: "", period_end: "", invoice_date: "",
  supplier: "",
};

export default function BuildingChargesTab({ building }) {
  const [charges, setCharges] = useState([]);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });

  const load = async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        api.get(`/charges?building_id=${building.id}`),
        api.get(`/lots?building_id=${building.id}`),
      ]);
      setCharges(c.data.items || []); setLots(l.data.items || []);
    } catch (err) {
      console.error("Load charges/lots failed:", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [building.id]);

  const create = async () => {
    try {
      const payload = {
        ...form, scope: "building", building_id: building.id,
        amount: parseFloat(form.amount) || 0,
      };
      ["period_start", "period_end", "invoice_date"].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await api.post("/charges", payload);
      toast.success("Charge enregistrée"); setOpen(false); setForm({ ...EMPTY }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
  };

  const del = async (id, status) => {
    if (status === "validated") {
      if (!window.confirm("Charge validée : impossible à supprimer. Archiver à la place ?")) return;
      try { await api.delete(`/charges/${id}?archive=true`); toast.success("Charge archivée"); load(); }
      catch { toast.error("Archivage impossible"); }
      return;
    }
    if (!window.confirm("Supprimer cette charge ?")) return;
    try { await api.delete(`/charges/${id}`); toast.success("Charge supprimée"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  const validate = async (id) => {
    if (!window.confirm("Valider cette répartition ? La charge ne pourra plus être modifiée ni recalculée.")) return;
    try { await api.post(`/charges/${id}/validate`); toast.success("Charge validée"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Validation impossible"); }
  };

  const lotName = (id) => lots.find((l) => l.id === id)?.reference || id.slice(0, 8);
  const totalAnnual = charges.reduce((a, c) => a + Number(c.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <StatBox label="Charges enregistrées" value={charges.length} />
        <StatBox label="Total annuel" value={formatEuro(totalAnnual)} />
        <StatBox label="Lots concernés" value={lots.length} />
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">Affectation des charges au bâtiment et répartition par lot.</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill" data-testid="charge-add-btn"><Plus className="mr-2 h-4 w-4" /> Nouvelle charge</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvelle charge</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Libellé</Label>
                <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="charge-form-label" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(CHARGE_CATEGORY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Montant (€)</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="charge-form-amount" />
                </div>
              </div>
              <div>
                <Label>Clé de répartition</Label>
                <Select value={form.allocation_key} onValueChange={(v) => setForm({ ...form, allocation_key: v })}>
                  <SelectTrigger data-testid="charge-form-allocation"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ALLOCATION_KEY_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Début période</Label><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} /></div>
                <div><Label>Fin période</Label><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} /></div>
                <div><Label>Date facture</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
              </div>
              <div><Label>Fournisseur</Label><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={create} data-testid="charge-form-submit">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {loading && <div className="text-sm text-muted-foreground py-4">Chargement…</div>}
        {!loading && charges.length === 0 && (
          <div className="card-elevated p-12 text-center text-muted-foreground">
            <Coins className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" /> Aucune charge sur ce bâtiment.
          </div>
        )}
        {charges.map((c) => (
          <div key={c.id} data-testid={`charge-row-${c.id}`} className="card-elevated p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-accent">{CHARGE_CATEGORY_LABELS[c.category] || c.category}</span>
                  <span className="text-xs text-muted-foreground">{ALLOCATION_KEY_LABELS[c.allocation_key] || c.allocation_key}</span>
                </div>
                <h3 className="mt-1 font-heading">{c.label}</h3>
                <div className="mt-1 text-xs text-muted-foreground">
                  {c.period_start && c.period_end ? `Période : ${formatDateFr(c.period_start)} → ${formatDateFr(c.period_end)}` : ""}
                  {c.invoice_date ? ` · Facture : ${formatDateFr(c.invoice_date)}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="font-serif text-2xl">{formatEuro(c.amount, true)}</div>
                <div className="mt-1 space-x-1">
                  {c.status === "validated"
                    ? <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-800">Validée</span>
                    : c.status === "archived"
                    ? <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">Archivée</span>
                    : <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-800">Brouillon</span>}
                </div>
                <div className="mt-2 space-x-1">
                  {c.status === "draft" && (
                    <Button variant="ghost" size="sm" onClick={() => validate(c.id)} data-testid={`charge-validate-${c.id}`} title="Valider la répartition">✓</Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => del(c.id, c.status)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            </div>
            {c.allocation_result && Object.keys(c.allocation_result).length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="text-overline mb-2">Répartition calculée</div>
                <div className="grid gap-1 sm:grid-cols-2 md:grid-cols-3 text-xs">
                  {Object.entries(c.allocation_result).map(([lotId, amount]) => (
                    <div key={lotId} className="flex justify-between rounded-lg bg-secondary/40 px-3 py-1.5">
                      <span className="truncate">{lotName(lotId)}</span>
                      <span className="font-mono">{formatEuro(amount, true)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-2xl border border-border bg-secondary/40 p-5">
      <div className="text-[10px] font-bold uppercase tracking-widest text-accent">{label}</div>
      <div className="mt-1 font-serif text-2xl">{value}</div>
    </div>
  );
}
